import type { IsoTimestamp } from '~/types/dates'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * A credential the user pasted, for a service Project Sola talks to (D-095 §1).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS IS THE ONLY STORE THAT HOLDS A SECRET, AND IT LEAVES THE DEVICE NOWHERE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  D-091 excludes credentials from every backup and every export, and D-095 §1
 *  applies it to this token by name. **That exclusion is enforced in
 *  `exports/backup/createBackup.ts`, not here**, because a model cannot stop a
 *  store being read — and until C7.0 it was enforced nowhere at all. The store
 *  was created empty at schema v3, the schema's own note claimed it was
 *  "excluded from backup and export", and `readAllStores` enumerates
 *  `objectStoreNames` and skips only `blobs`. The claim was true only because
 *  nothing ever wrote a record. **This file is the change that makes it false**,
 *  so the exclusion and its test land in the same commit.
 *
 *  The consequence is deliberate and the screen must say it plainly: **restoring
 *  a backup does not restore the token, and neither does restoring onto a new
 *  device.** A restore clears every store and rewrites from the payload, where
 *  `connections` is always empty, so the token is gone afterwards on any device
 *  including the one that made the backup. That is D-095's accepted cost —
 *  a credential in a backup file is a credential in whatever the user shares it
 *  with, and this store's whole point is that it is not there.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE TOKEN IS NOT A USER IDENTITY
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  One shared token across every device the user installs on. There are no
 *  accounts, no registration endpoint and no way to mint a credential (D-095 §1),
 *  so **revocation is all-or-nothing**: rotating the Worker secret signs out
 *  every device at once, which is the accepted cost of having no account system
 *  rather than an oversight.
 *
 *  There is deliberately no `lastUsedAt`, no call count and no success/failure
 *  history. D-095 §3 keeps the *server* to the current month and nothing else,
 *  and a client-side usage log would rebuild on the device exactly the timeline
 *  the server refuses to keep.
 */

/**
 * Providers Project Sola can hold a credential for.
 *
 * `fitbit` is listed because the store's natural key is the provider and D-097
 * is unwritten — naming it here costs nothing and makes "one connection per
 * provider" structural rather than a rule someone has to remember. **Nothing
 * reads it and nothing may write it until D-097 exists.**
 */
export const CONNECTION_PROVIDERS = ['solaWorker', 'fitbit'] as const

export type ConnectionProvider = (typeof CONNECTION_PROVIDERS)[number]

export interface ConnectionRecord extends RecordEnvelope {
  readonly provider: ConnectionProvider
  /**
   * The secret itself, exactly as the user pasted it.
   *
   * Stored verbatim and never hashed. A hash would be pointless theatre here:
   * the Worker compares the literal string, so the device must be able to send
   * it, and anything reversible on demand is not protection. **The real boundary
   * is the device**, which is the same answer D-083 gave for the unencrypted
   * backup and is recorded rather than dressed up.
   */
  readonly token: string
}

export interface ConnectionInput {
  readonly provider: string
  readonly token: string
}

/**
 * What counts as a token this app will store.
 *
 * Deliberately shallow: a length floor and no whitespace. **Project Sola does
 * not know what a valid Worker token looks like** — it is whatever string the
 * user set as the Worker secret — so any stricter rule here would be Sola
 * inventing a format the server never agreed to, and would reject a perfectly
 * good credential with a message the user cannot act on.
 *
 * The whitespace check earns its place from a real incident: a trailing newline
 * in `GEMINI_ENABLED` silently disabled the feature at C7.0 phase 2, because a
 * CLI added one and nothing trimmed it. A pasted token attracts the same
 * problem, and here the symptom would be `authenticationRefused` on a token
 * that looks correct on screen.
 */
export const MIN_TOKEN_LENGTH = 16

export function validateConnection(input: ConnectionInput): ValidationIssues {
  const issues = []

  if (!CONNECTION_PROVIDERS.includes(input.provider as ConnectionProvider)) {
    issues.push(issue('provider', 'That is not a service Project Sola connects to.'))
  }

  if (input.token.trim().length === 0) {
    issues.push(issue('token', 'Paste the access token from your Worker settings.'))
  } else if (input.token.trim().length < MIN_TOKEN_LENGTH) {
    issues.push(
      issue(
        'token',
        `That looks too short to be an access token — expected at least ${MIN_TOKEN_LENGTH} characters.`,
      ),
    )
  } else if (input.token.trim() !== input.token) {
    /* Reported rather than silently trimmed, because a token with spaces around
       it is more likely a partial copy than a clean one with stray padding, and
       repairing it quietly would hide that. `buildConnectionRecord` never sees
       an untrimmed value as a result. */
    issues.push(issue('token', 'That has a space or a line break at one end — copy it again.'))
  }

  return issues
}

export function buildConnectionRecord(
  input: ConnectionInput,
  now: IsoTimestamp,
  existing?: ConnectionRecord,
): Result<ConnectionRecord, ValidationIssues> {
  const issues = validateConnection(input)
  if (issues.length > 0) return err(issues)

  return ok({
    provider: input.provider as ConnectionProvider,
    token: input.token,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

/**
 * What the screen may show about a stored token.
 *
 * **Never the token.** A credential rendered into the DOM is a credential in a
 * screenshot, in a screen recording and in whatever the user is mirroring to —
 * and this project's own `INSTALL_TOKEN` reached a session transcript twice by
 * being visible where it did not need to be. The field is write-only from the
 * screen's point of view: you can replace it or clear it, and you can see that
 * something is stored, and that is the whole vocabulary.
 */
export interface ConnectionStatus {
  readonly connected: boolean
  /** Last four characters, for telling two tokens apart. Never more. */
  readonly hint?: string
}

export function connectionStatus(record: ConnectionRecord | undefined): ConnectionStatus {
  if (record === undefined) return { connected: false }
  return { connected: true, hint: record.token.slice(-4) }
}
